"use client"

import {
  performanceInsightFor,
  performanceMetricsFor,
} from "@/lib/question-quality/performance-insight"
import type { QuestionQualityHistory } from "@/lib/question-quality/types"
import { cn } from "@/lib/utils"
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card"

export function QuestionLeoPerformanceFacts({
  history,
  className,
}: {
  history: QuestionQualityHistory
  className?: string
}) {
  const metrics = performanceMetricsFor(history)
  if (metrics.length === 0) return null

  return (
    <dl className={cn("flex flex-col gap-2", className)}>
      {metrics.map((metric) => (
        <div
          key={metric.label}
          className="flex items-baseline justify-between gap-3 text-sm"
        >
          <dt className="text-muted-foreground">{metric.label}</dt>
          <dd
            className={cn(
              "inline-flex items-center gap-1 font-mono tabular-nums text-foreground",
              metric.tone === "caution" && "font-semibold text-destructive",
            )}
          >
            {metric.label === "Usage" && metric.tone === "caution" ? (
              <i
                className="fa-light fa-clock-rotate-left text-xs"
                aria-hidden="true"
              />
            ) : null}
            {metric.value}
          </dd>
        </div>
      ))}
    </dl>
  )
}

export function QuestionLeoUsagePerformanceHover({
  phrase,
  history,
}: {
  phrase: string
  history: QuestionQualityHistory
}) {
  const insight = performanceInsightFor(history)
  if (!insight) {
    return <span>{phrase}</span>
  }

  return (
    <HoverCard openDelay={120} closeDelay={80}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline rounded-sm font-inherit text-inherit underline decoration-dotted underline-offset-[0.2em]",
            "hover:text-brand focus-visible:text-brand",
            "focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
          )}
          aria-label={`${phrase}. Show performance for this item.`}
          onClick={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
        >
          {phrase}
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        side="bottom"
        sideOffset={8}
        collisionPadding={12}
        className="w-[min(17rem,calc(100vw-2rem))] p-3"
        onClick={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium text-foreground">Performance</p>
          {insight.examineeCount != null ? (
            <p className="text-xs leading-snug text-muted-foreground">
              Based on {insight.examineeCount} examinees.
            </p>
          ) : null}
          <QuestionLeoPerformanceFacts history={history} />
        </div>
      </HoverCardContent>
    </HoverCard>
  )
}

export function QuestionLeoPerformanceInsight({
  history,
}: {
  history: QuestionQualityHistory
}) {
  const insight = performanceInsightFor(history)
  if (!insight) return null

  return (
    <section
      className="flex flex-col gap-3 rounded-lg border border-border bg-muted/20 px-3.5 py-3"
      aria-labelledby="leo-performance-insight-heading"
    >
      <h3
        id="leo-performance-insight-heading"
        className="text-xs font-medium text-muted-foreground"
      >
        Performance
      </h3>
      {insight.examineeCount != null ? (
        <p className="text-xs leading-snug text-muted-foreground">
          Based on {insight.examineeCount} examinees.
        </p>
      ) : null}
      <QuestionLeoPerformanceFacts history={history} />
      <p className="text-xs leading-snug text-muted-foreground">{insight.hint}</p>
    </section>
  )
}
